import React from "react";
import { BlockMath, InlineMath } from "react-katex";

import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import FractionInput from "../../../../components/inputs/FractionInput";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import {
  getIntervalRaw,
  intervalsEqualList,
  parseIntervalsUnion,
  parseSingleInterval,
  randInt,
} from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/helpers/MaturaPiecewiseFunctionUtils";
import { samplePolynomialCurve } from "./helpers/smoothGraph";

const RELATIONS = [
  { latex: "f(x)>0", includeZero: false, wantsPositive: true },
  { latex: "f(x)<0", includeZero: false, wantsPositive: false },
  { latex: "f(x)\\ge 0", includeZero: true, wantsPositive: true },
  { latex: "f(x)\\le 0", includeZero: true, wantsPositive: false },
];

const isClose = (a, b) => Math.abs(a - b) < 1e-9;
const interval = (left, right, leftClosed, rightClosed) => ({
  left,
  right,
  leftClosed,
  rightClosed,
});

function intervalLatex(value) {
  return `${value.leftClosed ? "[" : "("}${value.left},\\,${value.right}${
    value.rightClosed ? "]" : ")"
  }`;
}

function intervalsLatex(values) {
  return values.map(intervalLatex).join("\\cup");
}

function buildData() {
  const h = randInt(-1, 1);
  const rootSide = Math.random() < 0.5 ? -1 : 1;
  const orientation = Math.random() < 0.5 ? -1 : 1;
  const zeroX = h + rootSide * 2;
  const outsideZero = h - rootSide * 2;

  const xL = rootSide > 0 ? h - 1 : h - 3;
  const xR = rootSide > 0 ? h + 3 : h + 1;
  const leftClosed = Math.random() < 0.5;
  const rightClosed = Math.random() < 0.5;
  const fn = (x) => orientation * ((x - h) * (x - h) - 4);
  const domainInterval = interval(xL, xR, leftClosed, rightClosed);

  const endpointValues = [
    { value: fn(xL), closed: leftClosed },
    { value: fn(xR), closed: rightClosed },
  ];
  const vertexValue = fn(h);
  const allValues = [vertexValue, ...endpointValues.map(({ value }) => value)];
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const minClosed =
    isClose(vertexValue, minValue) ||
    endpointValues.some(({ value, closed }) => isClose(value, minValue) && closed);
  const maxClosed =
    isClose(vertexValue, maxValue) ||
    endpointValues.some(({ value, closed }) => isClose(value, maxValue) && closed);
  const rangeInterval = interval(minValue, maxValue, minClosed, maxClosed);

  const possibleA = [];
  for (let x = xL + 1; x <= xR - 1; x += 1) {
    if (x !== zeroX) possibleA.push(x);
  }
  const a = possibleA[randInt(0, possibleA.length - 1)];

  const leftKind = orientation > 0 ? "decreasing" : "increasing";
  const rightKind = orientation > 0 ? "increasing" : "decreasing";
  const monotonicityKind = Math.random() < 0.5 ? leftKind : rightKind;
  const monotonicityInterval =
    monotonicityKind === leftKind
      ? interval(xL, h, leftClosed, true)
      : interval(h, xR, true, rightClosed);

  const relation = RELATIONS[randInt(0, RELATIONS.length - 1)];
  const testLeft = fn((xL + zeroX) / 2) > 0;
  const leftMatches = testLeft === relation.wantsPositive;
  const signIntervals = leftMatches
    ? [
        interval(
          xL,
          zeroX,
          leftClosed,
          relation.includeZero
        ),
      ]
    : [
        interval(
          zeroX,
          xR,
          relation.includeZero,
          rightClosed
        ),
      ];

  return {
    maxPoints: 6,
    h,
    a,
    valueAtA: fn(a),
    zeroX,
    relation,
    domainInterval,
    rangeInterval,
    monotonicityKind,
    monotonicityInterval,
    signIntervals,
    points: {
      A: {
        x: xL,
        y: fn(xL),
        markerSymbol: leftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      B: {
        x: xR,
        y: fn(xR),
        markerSymbol: rightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
    },
    curves: [samplePolynomialCurve(fn, xL, xR, [h, a, zeroX])],
    xRange: [-6, 6],
    yRange: [-6, 6],
  };
}

function intervalField(value, onChange, disabled, allowUnion = false) {
  return (
    <IntervalInput
      value={value || ""}
      onChange={onChange}
      allowUnion={allowUnion}
      disabled={disabled}
      showPreview={false}
    />
  );
}

function parsedNumber(value) {
  return value && typeof value === "object" ? value.parsed : Number(value);
}

function intervalCorrect(raw, expected, allowUnion = false) {
  const parsed = allowUnion ? parseIntervalsUnion(raw) : parseSingleInterval(raw);
  if (!parsed) return false;
  return intervalsEqualList(expected, Array.isArray(parsed) ? parsed : [parsed]);
}

export function generateTask() {
  return { type: "ReadGraphPropertiesCombinedTask", data: buildData() };
}

export function renderTask({ data }) {
  return (
    <div className="flex flex-col gap-3">
      <p>
        Na podstawie jednego wykresu wykonaj wszystkie polecenia. Za każdy
        poprawny podpunkt otrzymasz 1 punkt. <strong>(0–6 pkt)</strong>
      </p>
      <div style={{ margin: "0 auto", maxWidth: 330 }}>
        <GraphViewer
          width={330}
          points={data.points}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>
      <ol style={{ display: "grid", rowGap: 5, paddingLeft: 24 }}>
        <li>Podaj dziedzinę funkcji.</li>
        <li>Podaj zbiór wartości funkcji.</li>
        <li>Odczytaj <InlineMath math={`f(${data.a})`} />.</li>
        <li>Podaj miejsce zerowe funkcji.</li>
        <li>
          Podaj przedział, na którym funkcja jest{" "}
          <strong>
            {data.monotonicityKind === "increasing" ? "rosnąca" : "malejąca"}
          </strong>.
        </li>
        <li>
          Rozwiąż nierówność <InlineMath math={data.relation.latex} />.
        </li>
      </ol>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (key) => (value) =>
    onChange({ ...(userAnswer || {}), [key]: value });

  return (
    <div style={{ display: "grid", rowGap: 16 }}>
      <div>
        <div><strong>1.</strong> <InlineMath math={"x\\in"} /></div>
        {intervalField(userAnswer.domain, setField("domain"), disabled)}
      </div>
      <div>
        <div><strong>2.</strong> <InlineMath math={"y\\in"} /></div>
        {intervalField(userAnswer.range, setField("range"), disabled)}
      </div>
      <div>
        <div><strong>3.</strong> <InlineMath math={`f(${data.a})=`} /></div>
        <FractionInput
          value={userAnswer.value || ""}
          onChange={setField("value")}
          disabled={disabled}
          label={`f(${data.a})`}
        />
      </div>
      <div>
        <div><strong>4.</strong> <InlineMath math={"x_0="} /></div>
        <FractionInput
          value={userAnswer.zero || ""}
          onChange={setField("zero")}
          disabled={disabled}
          label="x_0"
        />
      </div>
      <div>
        <div><strong>5.</strong> <InlineMath math={"x\\in"} /></div>
        {intervalField(userAnswer.monotonicity, setField("monotonicity"), disabled)}
      </div>
      <div>
        <div>
          <strong>6.</strong>{" "}
          <InlineMath math={`${data.relation.latex}:\\quad x\\in`} />
        </div>
        {intervalField(userAnswer.sign, setField("sign"), disabled, true)}
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const domainOk = intervalCorrect(
    getIntervalRaw({ domain: userAnswer.domain }, "domain"),
    [data.domainInterval]
  );
  const rangeOk = intervalCorrect(
    getIntervalRaw({ range: userAnswer.range }, "range"),
    [data.rangeInterval]
  );
  const valueOk = isClose(parsedNumber(userAnswer.value), data.valueAtA);
  const zeroOk = isClose(parsedNumber(userAnswer.zero), data.zeroX);
  const monotonicityOk = intervalCorrect(
    getIntervalRaw({ monotonicity: userAnswer.monotonicity }, "monotonicity"),
    [data.monotonicityInterval]
  );
  const signOk = intervalCorrect(
    getIntervalRaw({ sign: userAnswer.sign }, "sign"),
    data.signIntervals,
    true
  );

  const parts = {
    domain: domainOk,
    range: rangeOk,
    value: valueOk,
    zero: zeroOk,
    monotonicity: monotonicityOk,
    sign: signOk,
  };
  const score = Object.values(parts).filter(Boolean).length;
  return { isCorrect: score === 6, score, maxScore: 6, parts };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ display: "grid", rowGap: 3 }}>
      <div><InlineMath math={`1.\\quad x\\in ${intervalLatex(data.domainInterval)}`} /></div>
      <div><InlineMath math={`2.\\quad y\\in ${intervalLatex(data.rangeInterval)}`} /></div>
      <div><InlineMath math={`3.\\quad f(${data.a})=${data.valueAtA}`} /></div>
      <div><InlineMath math={`4.\\quad x_0=${data.zeroX}`} /></div>
      <div><InlineMath math={`5.\\quad x\\in ${intervalLatex(data.monotonicityInterval)}`} /></div>
      <div><InlineMath math={`6.\\quad x\\in ${intervalsLatex(data.signIntervals)}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Końce wykresu wyznaczają dziedzinę.</div>
      <BlockMath math={`x\\in ${intervalLatex(data.domainInterval)}`} />
      <div><strong>Krok 2.</strong> Najmniejsza i największa wysokość wykresu wyznaczają zbiór wartości.</div>
      <BlockMath math={`y\\in ${intervalLatex(data.rangeInterval)}`} />
      <div><strong>Krok 3.</strong> Odczytujemy wskazaną wartość oraz przecięcie z osią <InlineMath math={"OX"} />.</div>
      <BlockMath math={`f(${data.a})=${data.valueAtA},\\qquad x_0=${data.zeroX}`} />
      <div><strong>Krok 4.</strong> Odczytujemy wskazany kierunek zmian wykresu.</div>
      <BlockMath math={`x\\in ${intervalLatex(data.monotonicityInterval)}`} />
      <div><strong>Krok 5.</strong> Dla nierówności wybieramy odpowiednią stronę miejsca zerowego.</div>
      <BlockMath math={`${data.relation.latex},\\qquad x\\in ${intervalsLatex(data.signIntervals)}`} />
    </div>
  );
}

function rawValue(value) {
  return value && typeof value === "object" ? value.raw : value;
}

export function renderUserAnswer(userAnswer = {}, task) {
  const data = task?.data;
  const show = (latex, value) => (
    <div>
      <InlineMath math={latex} />{" "}
      {value ? <InlineMath math={String(value)} /> : <i>(brak odpowiedzi)</i>}
    </div>
  );

  return (
    <div style={{ display: "grid", rowGap: 3 }}>
      {show("1.\\quad x\\in", getIntervalRaw({ domain: userAnswer.domain }, "domain"))}
      {show("2.\\quad y\\in", getIntervalRaw({ range: userAnswer.range }, "range"))}
      {show(`3.\\quad f(${data?.a})=`, rawValue(userAnswer.value))}
      {show("4.\\quad x_0=", rawValue(userAnswer.zero))}
      {show("5.\\quad x\\in", getIntervalRaw({ monotonicity: userAnswer.monotonicity }, "monotonicity"))}
      {show("6.\\quad x\\in", getIntervalRaw({ sign: userAnswer.sign }, "sign"))}
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
